import { useQuery } from "@tanstack/react-query";
import { getIntroduceCapacitiesByLanguage } from "apis/introduceCapacities";
import { getIntroduceCultureByLanguage } from "apis/introduceCultures";
import { getIntroduceFoundationalsByLanguage } from "apis/introduceFoundationals";
import { getIntroduceMenusByLanguage } from "apis/introduceMenus";
import { getIntroduceMessageByLanguage } from "apis/introduceMessages";
import { getIntroduceOverviewByLanguage } from "apis/introduceOverviews";
import { getIntroducePoliciesByLanguage } from "apis/introducePolicies";
import { getIntroduceVisionByLanguage } from "apis/introduceVisions";
import { getNavMenuByLanguage } from "apis/navMenus";
import { getSectionContentCardsByLanguage } from "apis/sectionContentCards";

export const introduceKeys = {
  all: ["introduce"],
  menus: (languageCode) => ["introduce", "menus", languageCode],
  overviewPage: (languageCode) => ["introduce", "overview-page", languageCode],
  overview: (languageCode) => ["introduce", "overview", languageCode],
  overviewCards: (languageCode) => [
    "introduce",
    "overview-cards",
    languageCode,
  ],
  capacities: (languageCode) => ["introduce", "capacities", languageCode],
  message: (languageCode) => ["introduce", "message", languageCode],
  foundationalsPage: (languageCode) => [
    "introduce",
    "foundationals-page",
    languageCode,
  ],
  foundationals: (languageCode) => ["introduce", "foundationals", languageCode],
  vision: (languageCode) => ["introduce", "vision", languageCode],
  culture: (languageCode) => ["introduce", "culture", languageCode],
  policies: (languageCode) => ["introduce", "policies", languageCode],
};

const getLanguageModule = async (languageCode) => {
  const langSuffix = languageCode === "vi" ? "_vi" : `_${languageCode}`;
  const module = await import(`../language/pages${langSuffix}`);
  return module[`pages${langSuffix}`];
};

const flattenMenus = (menus = []) =>
  menus.flatMap((menu) => [menu, ...flattenMenus(menu.children || [])]);

const getOverviewMenu = (menus = []) => {
  const menuList = flattenMenus(menus);

  return menuList.find((menu) => {
    const slug = (menu.slug || menu.Slug || "").toString().toLowerCase();
    const keyName = (menu.keyName || menu.KeyName || "")
      .toString()
      .toLowerCase();

    return (
      slug === "tong-quan" ||
      slug === "overview" ||
      keyName === "about_menu.overview"
    );
  });
};

const getCardSortOrder = (card, navMenuId) => {
  const pageSortOrder = (card.pages || []).find(
    (page) => Number(page.navMenuId) === Number(navMenuId),
  )?.sortOrder;

  return Number(card.sortOrder ?? card.pageSortOrder ?? pageSortOrder ?? 0);
};

export const INTRODUCE_CARD_GROUPS = {
  businessActivities: "CÁC HOẠT ĐỘNG KINH DOANH CHÍNH",
  cultureWorkplace: "Văn hóa và môi trường làm việc",
  salaryBenefits: "CHÍNH SÁCH LƯƠNG, THƯỞNG VÀ CÁC CHẾ ĐỘ ĐÃI NGỘ",
};

const normalizeCardGroupText = (value = "") =>
  value
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "d")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

export const filterIntroduceCardsBySubtitle = (cards = [], subtitle = "") => {
  const targetSubtitle = normalizeCardGroupText(subtitle);
  if (!targetSubtitle) return cards;

  const filtered = cards.filter((card) => {
    const cardSubtitle = normalizeCardGroupText(card.subtitle);
    if (!cardSubtitle) return false;

    if (cardSubtitle === targetSubtitle) return true;
    if (cardSubtitle.includes(targetSubtitle) || targetSubtitle.includes(cardSubtitle)) return true;

    // Keyword fallback matching for common section groups
    if (targetSubtitle.includes("van hoa") || targetSubtitle.includes("moi truong")) {
      return cardSubtitle.includes("van hoa") || cardSubtitle.includes("moi truong") || cardSubtitle.includes("culture");
    }
    if (targetSubtitle.includes("luong") || targetSubtitle.includes("thuong") || targetSubtitle.includes("dai ngo")) {
      return cardSubtitle.includes("luong") || cardSubtitle.includes("thuong") || cardSubtitle.includes("dai ngo") || cardSubtitle.includes("tuyen dung") || cardSubtitle.includes("policy") || cardSubtitle.includes("benefit");
    }
    if (targetSubtitle.includes("kinh doanh") || targetSubtitle.includes("hoat dong")) {
      return cardSubtitle.includes("kinh doanh") || cardSubtitle.includes("hoat dong") || cardSubtitle.includes("business");
    }

    return false;
  });

  return filtered;
};

export const useIntroduceMenusQuery = (languageCode) =>
  useQuery({
    queryKey: introduceKeys.menus(languageCode),
    queryFn: () => getIntroduceMenusByLanguage(languageCode),
    enabled: Boolean(languageCode),
  });

export const useIntroduceOverviewPageQuery = (languageCode) =>
  useQuery({
    queryKey: introduceKeys.overviewPage(languageCode),
    queryFn: async () => {
      try {
        const data = await getLanguageModule(languageCode);
        return data.introduce.overview_content;
      } catch {
        const module = await import("../language/pages_vi");
        return module.pages_vi.introduce.overview_content;
      }
    },
    enabled: Boolean(languageCode),
  });

export const useIntroduceOverviewQuery = (languageCode) =>
  useQuery({
    queryKey: introduceKeys.overview(languageCode),
    queryFn: () => getIntroduceOverviewByLanguage(languageCode),
    enabled: Boolean(languageCode),
  });

export const useIntroduceOverviewCardsQuery = (languageCode) =>
  useQuery({
    queryKey: introduceKeys.overviewCards(languageCode),
    queryFn: async () => {
      let overviewMenuId = null;
      try {
        const menus = await getNavMenuByLanguage(languageCode);
        const overviewMenu = getOverviewMenu(menus);
        overviewMenuId = overviewMenu?.id || null;
      } catch {
        overviewMenuId = null;
      }

      let cards = [];
      if (overviewMenuId) {
        cards = await getSectionContentCardsByLanguage(languageCode, overviewMenuId);
      }

      // Fallback: fetch all cards for language if overviewMenuId filtered query returned no items
      if (!cards || cards.length === 0) {
        cards = await getSectionContentCardsByLanguage(languageCode);
      }

      return [...(cards || [])].sort(
        (a, b) =>
          getCardSortOrder(a, overviewMenuId) -
          getCardSortOrder(b, overviewMenuId),
      );
    },
    enabled: Boolean(languageCode),
  });

export const useIntroduceCapacitiesQuery = (languageCode) =>
  useQuery({
    queryKey: introduceKeys.capacities(languageCode),
    queryFn: () => getIntroduceCapacitiesByLanguage(languageCode),
    enabled: Boolean(languageCode),
  });

export const useIntroduceMessageQuery = (languageCode) =>
  useQuery({
    queryKey: introduceKeys.message(languageCode),
    queryFn: () => getIntroduceMessageByLanguage(languageCode),
    enabled: Boolean(languageCode),
  });

export const useIntroduceFoundationalsPageQuery = (languageCode) =>
  useQuery({
    queryKey: introduceKeys.foundationalsPage(languageCode),
    queryFn: async () => {
      try {
        const data = await getLanguageModule(languageCode);
        return data.introduce.foundational_values.title;
      } catch {
        const module = await import("../language/pages_vi");
        return module.pages_vi.introduce.foundational_values.title;
      }
    },
    enabled: Boolean(languageCode),
  });

export const useIntroduceFoundationalsQuery = (languageCode) =>
  useQuery({
    queryKey: introduceKeys.foundationals(languageCode),
    queryFn: () => getIntroduceFoundationalsByLanguage(languageCode),
    enabled: Boolean(languageCode),
    select: (data) =>
      [...(data || [])].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
  });

export const useIntroduceVisionQuery = (languageCode) =>
  useQuery({
    queryKey: introduceKeys.vision(languageCode),
    queryFn: () => getIntroduceVisionByLanguage(languageCode),
    enabled: Boolean(languageCode),
  });

export const useIntroduceCultureQuery = (languageCode) =>
  useQuery({
    queryKey: introduceKeys.culture(languageCode),
    queryFn: () => getIntroduceCultureByLanguage(languageCode),
    enabled: Boolean(languageCode),
  });

export const useIntroducePoliciesQuery = (languageCode) =>
  useQuery({
    queryKey: introduceKeys.policies(languageCode),
    queryFn: () => getIntroducePoliciesByLanguage(languageCode),
    enabled: Boolean(languageCode),
  });
